import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import BagShopping from '@deliveroo/icon-set/react/BagShopping';
import Clock from '@deliveroo/icon-set/react/Clock';
import Hamburger from '@deliveroo/icon-set/react/Hamburger';
import Person from '@deliveroo/icon-set/react/Person';
import Location from '@deliveroo/icon-set/react/Location';

import i18n from '../../utils/i18n';
import { setOrderRating, dismissRating } from '../../actions/orderActions';
import { displayNotification } from '../../actions/notificationActions';

import OrderRatingModal from '../../components/OrderRatingModal';

export class OrderRating extends Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedRating: '',
      selectedRatingIssues: [],
      ratingComment: '',
      isModalOpen: false,
      submittedRating: false,
    };

    this.closeModal = this.closeModal.bind(this);
    this.openModal = this.openModal.bind(this);
    this.onRatingStarsChange = this.onRatingStarsChange.bind(this);
    this.onRatingIssuesChange = this.onRatingIssuesChange.bind(this);
    this.onRatingCommentChange = this.onRatingCommentChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.dismissRating = this.dismissRating.bind(this);
  }

  componentDidMount() {
    this.openModal();
  }

  onRatingStarsChange(evt) {
    this.setState({
      selectedRating: evt.target.value,
    });
  }

  onRatingIssuesChange(evt) {
    // eslint-disable-next-line react/no-access-state-in-setstate
    let issues = this.state.selectedRatingIssues;

    if (evt.target.checked) {
      issues.push(evt.target.value);
    } else {
      issues = issues.filter((item) => item !== evt.target.value);
    }

    this.setState({ selectedRatingIssues: issues });
  }

  onRatingCommentChange(text) {
    this.setState({ ratingComment: text });
  }

  onSubmit() {
    const { lastOrder, displayThankYouNotification } = this.props;
    const { selectedRatingIssues, selectedRating, ratingComment } = this.state;
    this.props.setOrderRating(lastOrder.id, selectedRating, selectedRatingIssues, ratingComment);
    this.setState({ submittedRating: true }, () => {
      this.closeModal();
      displayThankYouNotification({
        message: i18n.gettext('order_rating.thank_you_notification.text'),
        type: 'success',
      });
    });
  }

  dismissRating() {
    const { lastOrder } = this.props;
    this.props.dismissRating(lastOrder.id);
  }

  openModal() {
    this.setState({
      isModalOpen: true,
    });
  }

  closeModal() {
    if (!this.state.submittedRating) {
      this.dismissRating();
    }

    this.setState({
      isModalOpen: false,
    });
  }

  getRatingOptions = () => {
    const { isCollectionOrder } = this.props.lastOrder;

    const options = [
      {
        label: i18n.gettext('order_rating.options.arrival_time'),
        value: 'time',
        icon: Clock,
        selectable: !isCollectionOrder,
      },
      {
        label: i18n.gettext('order_rating.options.food'),
        value: 'food',
        icon: Hamburger,
        selectable: true,
      },
      {
        label: i18n.gettext('order_rating.options.collection_experience'),
        value: 'experience',
        icon: Location,
        selectable: isCollectionOrder,
      },
      {
        label: i18n.gettext('order_rating.options.packaging'),
        value: 'packaging',
        icon: BagShopping,
        selectable: true,
      },
      {
        label: i18n.gettext('order_rating.options.rider'),
        value: 'driver',
        icon: Person,
        selectable: !isCollectionOrder,
      },
    ];

    return options.filter((option) => option.selectable);
  };

  render() {
    const { lastOrder } = this.props;
    if (!lastOrder || !lastOrder.needsRating) return null;

    const ratingStrings = {
      title: i18n.gettext('order_rating.rate_order'),
      heading: i18n.gettext('order_rating.prompt_heading', {
        restaurantName: lastOrder.restaurantName,
      }),
      subheading: i18n.gettext('order_rating.prompt_subheading'),
      commentQuestionLowRating: i18n.gettext('order_rating.comment_question.low_rating'),
      commentQuestionHighRating: i18n.gettext('order_rating.comment_question.high_rating'),
      commentPlaceholderLowRating: i18n.gettext('order_rating.comment_placeholder.low_rating'),
      commentPlaceholderHighRating: i18n.gettext('order_rating.comment_placeholder.high_rating'),
      characterLimitError: i18n.gettext('order_rating.comment.character_limit_error'),
    };

    const isLowRatingSelected = this.state.selectedRating <= 3;

    return (
      <OrderRatingModal
        appElement="#__next"
        ratingStrings={ratingStrings}
        isButtonDisabled={!this.state.selectedRating}
        isLowRatingSelected={isLowRatingSelected}
        cancel={this.closeModal}
        selectedRating={this.state.selectedRating}
        onRatingStarsChange={this.onRatingStarsChange}
        showRatingIssues={!!this.state.selectedRating && isLowRatingSelected}
        showCommentBox={!!this.state.selectedRating}
        selectedRatingIssues={this.state.selectedRatingIssues}
        ratingComment={this.state.ratingComment}
        ratingOptions={this.getRatingOptions()}
        onRatingIssuesChange={this.onRatingIssuesChange}
        onRatingCommentChange={this.onRatingCommentChange}
        onModalClose={this.closeModal}
        onSubmit={this.onSubmit}
        isOpen={this.state.isModalOpen}
      />
    );
  }
}

OrderRating.propTypes = {
  lastOrder: PropTypes.shape({
    restaurantName: PropTypes.string,
    needsRating: PropTypes.bool,
    isCollectionOrder: PropTypes.bool,
  }),
  setOrderRating: PropTypes.func.isRequired,
  dismissRating: PropTypes.func.isRequired,
  displayThankYouNotification: PropTypes.func.isRequired,
};

OrderRating.defaultProps = {
  lastOrder: null,
};

const mapStateToProps = ({ order }) => ({
  lastOrder: order.results.all ? order.results.all[0] : null,
});

const mapDispatchToProps = (dispatch) => ({
  setOrderRating: (id, rating, issues, comment) =>
    dispatch(setOrderRating(id, rating, issues, comment)),
  dismissRating: (id) => dispatch(dismissRating(id)),
  displayThankYouNotification: ({ type, message }) =>
    dispatch(displayNotification({ type, message })),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(OrderRating);
